import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Alert, AlertTitle, Box, CircularProgress } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import CheckIcon from "@mui/icons-material/Check";
import ConfirmationDialog from "../../../components/ui/ConfirmationDialog";
import useApi from "../../../hooks/useApi";
import { useToaster } from "../../../hooks/useToaster";
import usePifDraft from "../../../hooks/usePifDraft";
import { useUser } from "../../../context/UserContext";
import { getPostIfApprovalChainStatus, getPostIfEmploymentHistory } from "../../../services/pifService";
import { PifReturnNote, derivePifReturnNote, getPifApprovalState } from "../../../services/pifListApproval";
import { PostIfApprovalChainStatusDto, PostIfEmploymentHistoryDto } from "../../../types/pifDto";
import { PostIfSignatureDto } from "../../../types/pifApprovalDto";
import { PIF_FLOW, SEVERITIES } from "../../../data/constants";
import { PIF_PDF_MESSAGES, POSTIF_TEXT } from "../../../constants/messages";
import { usePostIfPdfDownload } from "../../../hooks/usePostIfPdfDownload";
import PifPageBand from "./PifPageBand";
import { PIF_NOT_EDITABLE_STATE_KEY, PifRecordRouteState, copyPifRecordLink } from "./pifRecordLink";
import PifStartScreen from "./start/PifStartScreen";
import PifWorkspace from "./workspace/PifWorkspace";
import PifDoneScreen, { PifDoneChainItem } from "./done/PifDoneScreen";
import {
  PIF_BLANK_FORM,
  PifCandidate,
  PifEavValues,
  PifFlow,
  PifFormValues,
  PifScreen,
  REOPENABLE_PIF_STATUSES,
  formatPifSsn,
  omitBridgedFieldKeys,
  pifSavedPartialSsn,
} from "./types";
import "./PostInterviewForm.scss";

/** Portal_PostIF.Flow for the confirmed person — the approval chain branches on it. */
const FLOW_TO_API: Record<PifFlow, string> = {
  default: PIF_FLOW.NEW_HIRE,
  new: PIF_FLOW.NEW_HIRE,
  active: PIF_FLOW.EXISTING_ACTIVE,
  inactive: PIF_FLOW.REHIRE,
};

/** Reverse of FLOW_TO_API, for re-opening a saved draft. */
const API_TO_FLOW: Record<string, PifFlow> = {
  [PIF_FLOW.NEW_HIRE]: "new",
  [PIF_FLOW.EXISTING_ACTIVE]: "active",
  [PIF_FLOW.REHIRE]: "inactive",
};

/** Reverse of PifWorkspace's HIRE_TYPE_TO_SWITCH_OR_ADDITION. */
const SWITCH_OR_ADDITION_TO_HIRE_TYPE: Record<string, string> = {
  addition: "Additional position (keeps current one)",
  switch: "Position change (replaces current one)",
};

const LIST_URL = "/hiring/postif";

/**
 * The employee's CURRENT employment type, read off their active employment-history row.
 *
 * The identity lookup doesn't return it (PostIfIdentityLookupResultDto has no such field), so the
 * history is the only place an existing person's FT/PT arrives — without this the form always
 * opened on PIF_BLANK_FORM's "Part-time", even for someone plainly full-time. HR_EmploymentHistory
 * spells it "Full Time"/"Part Time"; matched loosely so a "Full-Time"/"FULLTIME" variant still
 * lands. Null means "the history doesn't say", which must leave the blank-form default alone
 * rather than assert part-time.
 */
const empTypeFromHistory = (rows: PostIfEmploymentHistoryDto[]): PifFormValues["empType"] | null => {
  const current = rows.find((row) => row.isActive);
  const value = (current?.employmentType ?? "").replace(/[\s-]/g, "").toLowerCase();
  if (value.includes("full")) return "Full-time";
  if (value.includes("part")) return "Part-time";
  return null;
};

/**
 * The post-submit chain. The first row is the person who just signed, so it has to be the real
 * signed-in user — it was a hardcoded name, which put someone else's signature on every
 * supervisor's receipt (PR 30856 review, Pooja Pujara). Resolved exactly as PifReviewDrawer does,
 * since that drawer is where the same person signed a moment earlier.
 */
const doneChain = (signerName: string, namesByRole: Record<string, string> = {}): PifDoneChainItem[] => [
  // Role, then approver name, then status, on every row (Bug 82350). The first row used to be the
  // signer's bare name with no role, which read as a different kind of entry from the three below.
  //
  // The three director names come from the real signature chain, which submit has already laid out
  // with their user ids resolved — they are knowable here, they were just never read. The local
  // signer name stays the fallback for row 1 so the receipt still names the person who just signed
  // even if the chain read fails, and each director falls back to blank, which renders as no name
  // line rather than an empty one.
  { role: "Supervisor", name: namesByRole["manager"] || signerName, detail: "Signed", state: "done" },
  { role: "Department Director", name: namesByRole["dept-director"] ?? "", detail: "Pending notified just now", state: "current" },
  { role: "HR Director", name: namesByRole["hr-director"] ?? "", detail: "Reviews HR details and approves the hire", state: "waiting" },
  { role: "Executive Director", name: namesByRole["executive-director"] ?? "", detail: "Provides the final approval", state: "waiting" },
  // Epic 82838: the fifth lifecycle stage, so the receipt shows the same five stages as the list
  // and the form's Signature History. Nobody signs it - HR reaches it by marking the approved form
  // as an employee - so it has no approver name, and right after submit it is always waiting.
  { role: "Completed", name: "", detail: "HR matches the form to an employee", state: "waiting" },
];

const PostInterviewForm: FC = () => {
  const { executeApi } = useApi();
  const dispatch = useDispatch();
  const showToaster = useToaster();
  const { download, downloadingId } = usePostIfPdfDownload();
  const { firstName, lastName, userName } = useUser();
  const [screen, setScreen] = useState<PifScreen>("start");
  const [flow, setFlow] = useState<PifFlow>("default");
  const [values, setValues] = useState<PifFormValues>({ ...PIF_BLANK_FORM });
  const [eav, setEav] = useState<PifEavValues>({});
  const [candidate, setCandidate] = useState<PifCandidate | null>(null);
  const [history, setHistory] = useState<PostIfEmploymentHistoryDto[]>([]);
  const [signed, setSigned] = useState<{ refNo: string; signedAt: string } | null>(null);
  // Approval-chain role token -> "First Last", read after submit so the done screen can name all
  // four approvers (Bug 82350). Empty until that read lands, and stays empty if it fails.
  const [approverNames, setApproverNames] = useState<Record<string, string>>({});
  const [reopenError, setReopenError] = useState<string | null>(null);
  // Set only for a form reopened in 'changes_requested' - the approver's note, so the supervisor
  // knows what to fix instead of guessing at a form that silently came back.
  const [returnNote, setReturnNote] = useState<PifReturnNote | null>(null);
  const [edited, setEdited] = useState(false);
  // Set when a reopened draft carried its own employment_type answer, so the employment-history
  // seed below leaves it alone. A ref, not state: it only ever gates an effect.
  const savedEmpType = useRef(false);
  const [confirmExit, setConfirmExit] = useState(false);
  const { draft, catalogue, opening, open, ensureCreated, load, reset: resetDraft } = usePifDraft();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const routerState = location.state as { pifCandidate?: PifCandidate | null } | null;
  /**
   * Epic 82836: /:id/signed is the post-submit receipt. It only has something to show in the
   * session that just signed - the receipt lives in this component's state - so any other visit
   * (a refresh, a bookmark, someone else's link) is sent to the read-only /:id view instead.
   */
  const isSignedRoute = location.pathname.endsWith("/signed");
  // Set by handleSigned so the effect below moves the URL to /signed only once the done screen is
  // committed. Navigating inside handleSigned could render /signed while `screen` was still "form",
  // which would trip the refresh redirect and throw away the receipt.
  const pendingSignedUrl = useRef(false);
  // True once this instance has shown a receipt. The /signed redirect is only for a visit that never
  // had one (refresh, bookmark, someone else's link) - not for "Start another" from the receipt.
  // React Router 7 applies navigations in a transition, so that click's state reset renders while
  // the URL is still /signed; without this, the redirect fired first and replaced the /new navigation.
  const hadReceipt = useRef(false);
  /**
   * The candidate handed over by the start screen when it resumed an existing draft, read once so
   * the reopen effect does not depend on `location` (it must not re-run when the state is later
   * replaced by a navigation). A ref, not state: it only ever feeds that one effect.
   */
  const resumedCandidate = useRef<PifCandidate | null>(routerState?.pifCandidate ?? null);
  const reopenId = Number(id);
  const isReopen = !!reopenId && !Number.isNaN(reopenId);
  // Hold the whole screen while re-opening, so the start gate never shows for a saved draft.
  // Owned by the reopen effect below rather than derived from `draft`: usePifDraft.load sets
  // `draft` as soon as the read returns, which is before this component has moved off the
  // "start" screen — and under StrictMode's double-mount the first pass leaves `draft` set
  // while the second pass is still calling can-act + get, so the lookup gate rendered for the
  // whole of that second load instead of the draft the supervisor clicked Edit on.
  const [hydrating, setHydrating] = useState(isReopen);
  // Bug 83231: a new form checks the approval chain before the start screen shows, so a user whose
  // Reports-To hierarchy can't resolve one is told so up front instead of failing at Submit (-7).
  const [checkingChain, setCheckingChain] = useState(!isReopen);
  const [chainUnresolvable, setChainUnresolvable] = useState(false);

  const handleFieldChange = useCallback((patch: Partial<PifFormValues>) => {
    setEdited(true);
    setValues((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleEavChange = useCallback((fieldKey: string, value: string) => {
    setEdited(true);
    setEav((prev) => ({ ...prev, [fieldKey]: value }));
  }, []);

  /**
   * Epic 79822's leave-without-saving guard. Leaving unmounts the form and drops everything
   * typed, so only skip the prompt when nothing has been entered yet. `edited` is cleared on
   * a successful save, so a saved draft exits without nagging.
   *
   * Nothing has to be cleaned up on the way out any more: since Bug 79824 the Portal_PostIF row
   * is only written on the first save, so a form the supervisor abandons never existed.
   */
  const handleExit = useCallback(() => {
    if (edited) {
      setConfirmExit(true);
      return;
    }
    navigate(LIST_URL);
  }, [edited, navigate]);

  const handleConfirmExit = useCallback(
    (confirmed: boolean) => {
      setConfirmExit(false);
      if (confirmed) navigate(LIST_URL);
    },
    [navigate],
  );

  /** Stable so it doesn't re-create PifWorkspace's persistDraft callback on every render. */
  const handleSaved = useCallback(() => setEdited(false), []);

  /**
   * Epic 82836: once Save draft has written the row, the address becomes that record's own edit
   * URL, so bookmarking or copying it opens this form rather than a blank /new. `replace` keeps
   * /new out of the history. The reopen effect sees the id is already the one in memory and does
   * not re-load it. Not called from Sign & submit - that path goes straight to /signed.
   */
  const handleDraftSaved = useCallback(
    (postIfId: number) => {
      if (reopenId !== postIfId) navigate(`${LIST_URL}/${postIfId}/edit`, { replace: true });
    },
    [navigate, reopenId],
  );

  /**
   * The lookup found an open draft this user already owns for the person, so reopen it at its
   * own URL instead of creating a duplicate. Routing here (rather than hydrating inline) reuses
   * the reopen effect below, which restores applicant, contact, kind-of-hire and every answer.
   *
   * Must be `/:id/edit`, not `/:id`: since Epic 79822, the bare id renders the read-only
   * PostIFApproval detail screen, and only `/edit` renders this form.
   */
  const handleResume = useCallback(
    (postIfId: number, cand: PifCandidate | null) => {
      // The candidate rides along in router state. Resuming REMOUNTS this component at /:id/edit,
      // so component state is gone by the time the reopen effect runs - and that effect can only
      // rebuild the candidate from a stored EnterpriseEmployeeId. Where that column is still NULL
      // (a draft first saved without it) the id would be lost for good: every later save sends
      // null and the sproc's ISNULL(@New, Existing) keeps the null. This hands it the value.
      navigate(`${LIST_URL}/${postIfId}/edit`, { state: { pifCandidate: cand } });
    },
    [navigate],
  );

  /**
   * The person is confirmed, so this is where the record starts existing: create-draft
   * inserts the header and takes the soft record-lock. If it fails, useApi has already
   * raised the alert — stay on the start screen rather than opening a form that cannot save.
   */
  const handleConfirmed = useCallback(
    async (nextFlow: PifFlow, cand: PifCandidate | null, lookupValue?: string) => {
      const opened = await open({
        flow: FLOW_TO_API[nextFlow],
        postIfTitle: cand ? `${cand.first ?? ""} ${cand.last ?? ""}`.trim() || null : null,
        deptPositionId: null,
      });
      if (!opened) return;

      setCandidate(cand);
      setFlow(nextFlow);
      if (nextFlow === "new") {
        // Keep the SSN the start screen handed over (q) so the Applicant section can show it back,
        // masked — it is the only place a brand-new applicant's SSN arrives (there is no candidate
        // record). It is nine digits or nothing: a shorter entry was a hiring number that matched
        // no record, and the start screen no longer passes it off as an SSN (Bug 82668).
        const lookupSsn = lookupValue ?? "";
        setValues((prev) => ({
          ...prev,
          hireType: "New employee",
          q: lookupSsn,
          // Recorded here, at the hand-off, rather than derived from q's length later: the field is
          // editable when this is false, so q grows to nine digits as the supervisor types and a
          // length check would lock the input under them.
          ssnFromLookup: lookupSsn.replace(/\D/g, "").length === 9,
        }));
      } else if (cand) {
        setValues((prev) => ({
          ...prev,
          firstName: cand.first || "",
          middleName: cand.middle || "",
          lastName: cand.last || "",
          dob: cand.dob || "",
          addr1: cand.addr1 || "",
          city: cand.city || "",
          state: cand.state || "NY",
          zip: cand.zip || "",
          home: cand.home || "",
          cell: cand.cell || "",
          email: cand.email || "",
          hireType: nextFlow === "active" ? "Additional position (keeps current one)" : "New employee",
        }));
      }
      setScreen("form");
    },
    [open],
  );

  /**
   * Re-open an existing PostIF from /hiring/postif/:id.
   *
   * Restores the applicant, contact, kind-of-hire and every saved dynamic answer. It does
   * NOT restore Department/Position: `Portal_PostIF` stores only `DeptPositionId`, and no
   * endpoint maps a position back to its department, so the two dropdowns can't be
   * repopulated — the supervisor re-picks them. (The header write must therefore leave
   * DeptPositionId alone when it arrives as null, or re-saving would wipe it.)
   *
   * Only the creator gets this far: can-act 'edit' refuses everyone else (Bug 84558). It does not
   * check status - save and submit refuse a locked form with 409 - so the creator of a form that
   * is no longer a draft still loads it. Such a form is read-only per the Epic, so it is then sent
   * to the read-only /:id view rather than an editable form (Bug 84564).
   */
  useEffect(() => {
    if (!reopenId || Number.isNaN(reopenId)) return;
    // Epic 82836: the URL now follows the record (Save draft -> /:id/edit, submit -> /:id/signed),
    // so an id change can be this same form catching up with its own save. It is already hydrated
    // - re-loading would overwrite what is on screen, and on /signed would put a locked form back
    // into edit mode. A fresh visit to /signed is redirected in the render below, never loaded.
    if (isSignedRoute || draft?.postIfId === reopenId) {
      setHydrating(false);
      return;
    }
    let active = true;

    const reopen = async () => {
      const result = await load(reopenId);
      if (!active) return;
      // Bug 84558: can-act 'edit' refused - only the creator may edit. Send them to the read-only
      // view instead of leaving them on /edit; it settles what they may see (the record, or its own
      // "no longer valid" screen).
      if (result.kind === "refused") {
        showToaster(POSTIF_TEXT.CANNOT_EDIT, SEVERITIES.WARNING);
        navigate(`${LIST_URL}/${reopenId}`, { replace: true });
        return;
      }
      // A failed call is not a refusal: useApi has already shown the real error, and a toast here
      // would replace it (one alert slot) with a wrong "you can't edit" (PR 33216).
      if (result.kind === "error") {
        setReopenError(POSTIF_TEXT.REOPEN_LOAD_ERROR);
        setHydrating(false);
        return;
      }

      const { detail } = result;
      const { header, applicant, answers } = detail;
      setReturnNote(derivePifReturnNote(detail));
      setFlow(API_TO_FLOW[header.flow ?? ""] ?? "new");
      const savedAnswers = (answers ?? []).reduce<PifEavValues>((map, answer) => {
        if (answer.fieldKey && typeof answer.value === "string") map[answer.fieldKey] = answer.value;
        return map;
      }, {});
      // Bridged keys are owned by `f`, NOT by `eav`: PifSectionDynamic excludes them, so the user
      // can never edit them through the dynamic section. Seeding them into `eav` anyway made the
      // stale saved copy win in persistDraft's `{ ...derived, ...eav }` ("an explicit eav entry
      // always wins"), so on a REOPENED draft every later edit to Reports to / Service / Hours per
      // week / Rate type was silently discarded and the value first saved was written back
      // unchanged. Keep only the generic Additional-details answers here.
      setEav(omitBridgedFieldKeys(savedAnswers));
      if (savedAnswers.employment_type) savedEmpType.current = true;

      // Mirror of PifWorkspace's derived-answer bridge. These three keys live in the EAV
      // catalogue but are rendered by hardcoded controls bound to `f`, so without mapping them
      // back they reload blank even though they saved correctly.
      setValues((prev) => ({
        ...prev,
        // Position needs its department set first, or the Position dropdown stays disabled.
        deptId: header.departmentId ?? prev.deptId,
        posId: header.deptPositionId ?? prev.posId,
        rate: savedAnswers.requested_rate ?? prev.rate,
        startDate: savedAnswers.desired_start_date ?? prev.startDate,
        notes: savedAnswers.department_notes ?? prev.notes,
        hours: savedAnswers.hours_per_week ?? prev.hours,
        // Stored as the label. Anything other than an exact "Full-time" (including a draft saved
        // before the bridge existed, which has no answer at all) falls back to the blank form's
        // Part-time rather than widening the union.
        empType: savedAnswers.employment_type === "Full-time" ? "Full-time" : prev.empType,
        // The driving question. Narrowed back onto its union rather than cast: anything that is not
        // an exact 'yes'/'no' (including no answer at all, e.g. a draft saved before it was bridged)
        // must leave the blank-form default alone rather than widen the type. A stored has_license
        // is ignored - Epic 82836 dropped that question.
        licNum: savedAnswers.license_number ?? prev.licNum,
        vehicle:
          savedAnswers.will_operate_vehicle === "yes" || savedAnswers.will_operate_vehicle === "no"
            ? savedAnswers.will_operate_vehicle
            : prev.vehicle,
        // Stored as the id; the matching label is backfilled from the rate-type lookup in
        // PifWorkspace once it loads, since this screen has no reference data of its own.
        rateTypeId: savedAnswers.rate_type ? Number(savedAnswers.rate_type) : prev.rateTypeId,
        // serviceId must be restored for the Client dropdown to re-enable at all - it is
        // disabled={!f.serviceId} and its options are fetched by GetClientsByService(serviceId).
        serviceId: savedAnswers.service ? Number(savedAnswers.service) : prev.serviceId,
        clientId: savedAnswers.client ? Number(savedAnswers.client) : prev.clientId,
        reportsToId: savedAnswers.reports_to ? Number(savedAnswers.reports_to) : prev.reportsToId,
      }));

      if (applicant) {
        // Bug 83251: a draft saved with fewer than nine digits comes back unmasked (only a full SSN
        // is masked), so it reopens in the editable field for the supervisor to finish.
        const partialSsn = pifSavedPartialSsn(applicant.ssn);
        setValues((prev) => ({
          ...prev,
          firstName: applicant.firstName ?? "",
          middleName: applicant.middleName ?? "",
          lastName: applicant.lastName ?? "",
          knownAs: applicant.knownName ?? "",
          // Bug 82803: already masked by the API ('***-**-####'), never the raw SSN. `q` cannot be
          // restored - it only ever holds what was typed this session - so this is the sole way a
          // reopened form can show the SSN back instead of an empty placeholder.
          // Bug 83251: except a partial SSN, which goes back into `q` - leaving ssnMasked empty is
          // what keeps the field editable (isPifSsnEditable).
          ssnMasked: partialSsn ? "" : applicant.ssn ?? "",
          q: partialSsn ? formatPifSsn(partialSsn) : prev.q,
          dob: applicant.dob ? applicant.dob.slice(0, 10) : "",
          addr1: applicant.address1 ?? "",
          addr2: applicant.address2 ?? "",
          city: applicant.city ?? "",
          state: applicant.state ?? "NY",
          zip: applicant.zip ?? "",
          home: applicant.homePhone ?? "",
          cell: applicant.cell ?? "",
          email: applicant.email ?? "",
          hireType: SWITCH_OR_ADDITION_TO_HIRE_TYPE[header.switchOrAddition ?? ""] ?? "New employee",
        }));
        // Prefer the stored id; fall back to the one the start screen handed over when it resumed
        // this draft (see handleResume). Without the fallback, a draft whose
        // EnterpriseEmployeeId is NULL can never acquire one - candidate stays null, save-applicant
        // keeps sending null, and ISNULL(@New, Existing) keeps the null. `candidate` is what
        // PifWorkspace reads for the save, so this is what actually repairs the column.
        const employeeId = applicant.enterpriseEmployeeId ?? resumedCandidate.current?.employeeId ?? null;
        if (employeeId) {
          setCandidate({
            status: header.flow === PIF_FLOW.EXISTING_ACTIVE ? "active" : "inactive",
            employeeId,
            first: applicant.firstName ?? resumedCandidate.current?.first ?? "",
            last: applicant.lastName ?? resumedCandidate.current?.last ?? "",
          });
        }
      }

      // 'changes_requested' reopens EDITABLE - that is the whole point of returning a form. Only
      // a genuinely locked status (submitted / approved / declined) lands on the read-only screen.
      // The API agrees: both IU procs and Submit_U accept 'draft' and 'changes_requested' alike.
      // Bug 84564: that is the record's read-only /:id view with a "can no longer be edited" note -
      // not this form's "Signed and sent" receipt, which read as if the user had just submitted it.
      // can-act 'edit' is status-agnostic (creator only since Bug 84558), so a typed URL, Back, or an
      // old edit link reaches here for any locked form of theirs. `replace` keeps the dead /edit
      // entry out of the history.
      if (header.status && !REOPENABLE_PIF_STATUSES.includes(header.status.toLowerCase())) {
        const state: PifRecordRouteState = { [PIF_NOT_EDITABLE_STATE_KEY]: true };
        navigate(`${LIST_URL}/${reopenId}`, { replace: true, state });
        return;
      }
      setScreen("form");
      setHydrating(false);
    };

    void reopen();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reopenId]);

  // Only an explicit `resolvable: false` blocks. A failed or unavailable check opens the form as
  // before — submit still refuses an unresolvable chain, so this can only ever tell the user sooner.
  useEffect(() => {
    if (isReopen) return;
    let active = true;

    const check = async () => {
      const status = await executeApi<undefined, PostIfApprovalChainStatusDto | null>(
        getPostIfApprovalChainStatus,
        undefined,
      );
      if (!active) return;
      setChainUnresolvable(status?.resolvable === false);
      setCheckingChain(false);
    };

    void check();
    return () => {
      active = false;
    };
  }, [executeApi, isReopen]);

  // The employment history behind the "Already at Hamaspik" card. Only an existing person
  // has one, and it is advisory — a failure must not stop the form.
  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!candidate?.employeeId) {
        setHistory([]);
        return;
      }
      const rows = await executeApi<number, PostIfEmploymentHistoryDto[]>(
        getPostIfEmploymentHistory,
        candidate.employeeId,
      );
      if (!active) return;
      setHistory(rows ?? []);

      // Seed the FT/PT toggle from what they're working today. Skipped once the draft's own saved
      // answer has been restored — that was an explicit choice about the NEW position and outranks
      // the current one. PifWorkspace clamps this back to Part-time if the position finally chosen
      // can't be full-time, so seeding "Full-time" here can't smuggle an invalid value through.
      if (!savedEmpType.current) {
        const fromHistory = empTypeFromHistory(rows ?? []);
        if (fromHistory) setValues((prev) => ({ ...prev, empType: fromHistory }));
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [executeApi, candidate?.employeeId]);

  const handleRestart = useCallback(() => {
    setValues({ ...PIF_BLANK_FORM });
    setEav({});
    setCandidate(null);
    setHistory([]);
    setFlow("default");
    setSigned(null);
    // Or the next form's receipt would open showing the previous one's approvers.
    setApproverNames({});
    setReturnNote(null);
    // Clears the id AND the pending create request, so "Start another" begins a genuinely new
    // form rather than saving the next person's answers onto the one just submitted.
    resetDraft();
    setEdited(false);
    setScreen("start");
    // Off the previous record's /:id/signed (or /:id/edit) URL, so the address matches the blank form.
    navigate(`${LIST_URL}/new`, { replace: true });
  }, [resetDraft, navigate]);

  const handleSigned = useCallback(
    (refNo: string, signedAt: string) => {
      setSigned({ refNo, signedAt });
      setScreen("done");
      pendingSignedUrl.current = true;
      hadReceipt.current = true;

      // Read the chain submit just laid out, so the receipt can name all four approvers and not
      // only the person who signed (Bug 82350). Deliberately not awaited and never blocking: the
      // submit has already succeeded and the screen is up, so a failure here must cost the user
      // nothing. On failure — or before the API half of 82350 ships, when the rows come back with
      // no name on them — approverNames stays empty and each row falls back to no name line.
      const postIfId = draft?.postIfId;
      if (!postIfId) return;

      void (async () => {
        const rows = await executeApi<number, PostIfSignatureDto[]>(getPifApprovalState, postIfId);
        if (!rows?.length) return;

        setApproverNames(
          rows.reduce<Record<string, string>>((acc, row) => {
            const roleKey = (row.role ?? "").toLowerCase();
            const name = `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim();
            if (roleKey && name) acc[roleKey] = name;
            return acc;
          }, {}),
        );
      })();
    },
    [draft?.postIfId, executeApi],
  );

  // Epic 82836: after Sign & submit the address becomes the clean /:id/signed receipt URL. Runs
  // after the done screen has committed - see pendingSignedUrl. `replace` drops the /edit entry, so
  // Back does not land on an edit URL for a form that is now locked.
  useEffect(() => {
    const postIfId = draft?.postIfId;
    if (screen !== "done" || !pendingSignedUrl.current || !postIfId) return;
    pendingSignedUrl.current = false;
    navigate(`${LIST_URL}/${postIfId}/signed`, { replace: true });
  }, [screen, draft?.postIfId, navigate]);

  // Off /signed (e.g. after Start another lands on /new), so a later /signed visit is judged afresh.
  useEffect(() => {
    if (!isSignedRoute) hadReceipt.current = false;
  }, [isSignedRoute]);

  // Epic 82836: this must stay the permanent /:id - never /signed (a one-time receipt) or /edit.
  // Shared with the approvers' done screens (Bug 83237) — see pifRecordLink for why the link is
  // the record's own /:id route and not window.location.href.
  const handleCopyLink = useCallback(() => copyPifRecordLink(draft?.postIfId, dispatch), [draft?.postIfId, dispatch]);

  // Epic 83470: the branded server PDF, named after the ref submit returned (or create-draft's).
  const handleDownloadPdf = useCallback(
    () => download(draft?.postIfId ?? 0, signed?.refNo || draft?.refNo),
    [download, draft?.postIfId, draft?.refNo, signed?.refNo],
  );
  // Bug 82787: the supervisor's done screen gets the same way back to the list as the approvers'.
  const handleBackToQueue = useCallback(() => navigate(LIST_URL), [navigate]);

  // A /:id/signed visit with no receipt in memory (refresh, bookmark, shared link) shows the
  // read-only record instead - Epic 82836: it must render, never error. That route is server-guarded.
  if (isSignedRoute && !hadReceipt.current) {
    return <Navigate to={`${LIST_URL}/${id}`} replace />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className="postif-shell">
        {/* Above the screens, so it shows on the gate, the form and the done screen alike — the
            trail is also the only way back to the list from the form. It scrolls away under
            PifWorkspace's sticky header rather than fighting it for the top slot; that header's
            offset is measured off the shell header, not off this. */}
        {/* The URL carries the id from the first save on (Epic 82836), so isReopen alone would label
            the signed receipt "Edit draft"; the done screen is checked first. */}
        <PifPageBand
          title="Post Interview Form"
          current={screen === "done" ? "Signed" : isReopen ? "Edit draft" : "New PostIF"}
        />

        {/* Holds the screens under the band, each of which decides for itself whether it rides up
            into the band's lower edge (see PostInterviewForm.scss). */}
        <Box className="postif-shell__body">
          {/* Sits above the workspace, aligned to its grid (maxWidth mirrors $pif-content-max-width).
              Gated on the form screen so it never trails the supervisor onto the done screen after a
              successful re-submit. */}
          {screen === "form" && returnNote && (
            <Alert
              severity="warning"
              sx={{ maxWidth: 1560, mx: "auto", mt: 1, mb: -0.5 }}
              data-testid="postif-returned-note"
            >
              <AlertTitle sx={{ mb: 0.25 }}>
                Returned for changes by the {returnNote.by}
                {returnNote.when ? ` · ${returnNote.when}` : ""}
              </AlertTitle>
              {returnNote.reason || "No note was left. Ask them what needs to change before re-submitting."}
            </Alert>
          )}

          {reopenError ? (
            <Box sx={{ p: 4, maxWidth: 640, mx: "auto" }}>
              <Alert severity="error" data-testid="postif-reopen-error">
                {reopenError}
              </Alert>
            </Box>
          ) : chainUnresolvable ? (
            <Box sx={{ p: 4, maxWidth: 640, mx: "auto" }}>
              <Alert severity="error" data-testid="postif-approval-chain-error">
                {POSTIF_TEXT.APPROVAL_CHAIN_UNRESOLVABLE}
              </Alert>
            </Box>
          ) : hydrating || checkingChain ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", gap: 1.5 }}>
              <CircularProgress size={20} thickness={5} />
              <span>Opening the form…</span>
            </Box>
          ) : (
            <>
              {screen === "start" && (
                <PifStartScreen onConfirmed={handleConfirmed} onResume={handleResume} opening={opening} />
              )}
              {screen === "form" && (
                <PifWorkspace
                  flow={flow}
                  candidate={candidate}
                  values={values}
                  draft={draft}
                  ensureDraftId={ensureCreated}
                  catalogue={catalogue}
                  eav={eav}
                  history={history}
                  onChange={handleFieldChange}
                  onEavChange={handleEavChange}
                  onSaved={handleSaved}
                  onDraftSaved={handleDraftSaved}
                  onExit={handleExit}
                  onSigned={handleSigned}
                />
              )}
              {screen === "done" && signed && (
                <PifDoneScreen
                  icon={<CheckIcon />}
                  tone="success"
                  title="Signed and sent"
                  lede="It's locked and is now with the Department Director. After that, HR reviews it, and the Executive Director signs it. A completed copy is then sent back to you."
                  // Epic 79824's generic done-screen props, fed the real reference number that
                  // submit returned (or the one create-draft gave us) rather than a stub id.
                  meta={[signed.refNo || draft?.refNo, signed.signedAt && `signed ${signed.signedAt}`]
                    .filter(Boolean)
                    .join(" · ")}
                  chain={doneChain(`${firstName ?? ""} ${lastName ?? ""}`.trim() || userName || "—", approverNames)}
                  actions={[
                    { label: "Copy link", variant: "outlined", onClick: handleCopyLink, testId: "postif-final-copylink" },
                    {
                      label: downloadingId !== null ? PIF_PDF_MESSAGES.PREPARING : "Download PDF",
                      variant: "outlined",
                      onClick: handleDownloadPdf,
                      testId: "postif-final-downloadpdf",
                      disabled: downloadingId !== null,
                    },
                    // Epic 82838: "Back to my list" - one name for the way back from every PostIF screen.
                    // The testId keeps its original name so existing specs still find it.
                    { label: "Back to my list", variant: "outlined", onClick: handleBackToQueue, testId: "postif-final-backtoqueue" },
                    { label: "Start another", variant: "contained", onClick: handleRestart, testId: "postif-final-startanother" },
                  ]}
                />
              )}
            </>
          )}
        </Box>

        {/*
          Bug 82804: discarding is the destructive choice here, so focus starts on No — Enter
          straight after the popup opens must not throw the supervisor's answers away.
        */}
        <ConfirmationDialog
          open={confirmExit}
          title={POSTIF_TEXT.DISCARD_CONFIRM_TITLE}
          message={POSTIF_TEXT.DISCARD_CONFIRM_MESSAGE}
          initialFocus="no"
          onClose={handleConfirmExit}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default PostInterviewForm;
